import React, { useEffect, useRef, useState } from 'react';
import theme from '../../theme';
import { fetchScannerLibrary } from '../../services/api';

/**
 * Folder tiles for COUNTER — the same "pick one, then work inside it" step the
 * account grid gives the trading views. Managing what is in them lives in the
 * library behind its own button.
 */
const ScannerFolderList = ({
  onOpen, reloadSignal = 0, activeId = null, onLoaded = () => {}, onFailed = () => {},
}) => {
  const [library, setLibrary] = useState(null);
  // The latest callbacks, not the ones of the render that started the fetch.
  const callbacks = useRef({ onLoaded, onFailed });
  callbacks.current = { onLoaded, onFailed };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchScannerLibrary();
        if (cancelled) return;
        if (!data) {
          setLibrary((prev) => prev || { folders: [], instruments: [] });
          callbacks.current.onFailed();
          return;
        }
        setLibrary(data);
        callbacks.current.onLoaded(data);
      } catch (e) {
        // A failed reload keeps the tiles already on screen.
        if (cancelled) return;
        setLibrary((prev) => prev || { folders: [], instruments: [] });
        callbacks.current.onFailed();
      }
    })();
    return () => { cancelled = true; };
  }, [reloadSignal]);

  if (!library) {
    return (
      <div className="flex justify-center py-8">
        <div className={`h-6 w-6 animate-spin rounded-full border-2 ${theme.spinner}`} />
      </div>
    );
  }

  if (library.folders.length === 0) {
    return (
      <div className={`rounded-xl ${theme.cardBg} ${theme.cardBorder} p-6 text-center text-sm ${theme.textMuted}`}>
        No folders yet. Open the library to create one.
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2.5">
      {library.folders.map((folder) => {
        const instruments = library.instruments.filter((i) => i.folder_id === folder.id);
        const active = folder.id === activeId;
        return (
          <button
            key={folder.id}
            onClick={() => !active && onOpen(folder, instruments)}
            title={folder.name}
            className={`group flex w-28 items-center gap-2 px-3 py-2.5 ${theme.cardBg} rounded-xl ${active ? 'border-2 border-olive-500' : theme.cardBorder} cursor-pointer text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-olive-300 hover:shadow-md active:scale-[0.98]`}
          >
            {/* Fixed width fits about three letters; the rest goes to the ellipsis and the hover title. */}
            <h3 className={`min-w-0 truncate text-sm font-bold ${theme.textPrimary} tracking-wide group-hover:text-olive-600`}>
              {folder.name}
            </h3>
            <svg className="ml-auto h-4 w-4 flex-shrink-0 text-olive-300 group-hover:text-olive-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        );
      })}
    </div>
  );
};

export default ScannerFolderList;
